import { useEffect, useRef, useState } from 'react';
import { getThemedChat, InstanceID, User } from './helpers';

interface UseTalkJSInterface {
  theme: string;
  user: User;
  instanceID: InstanceID;
}

const useTalkJS = ({ theme, user, instanceID }: UseTalkJSInterface) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let activeContainer: HTMLDivElement | null = null;

    const alignGhost = () => {
      if (!shellRef.current || !activeContainer) return;
      const rect = shellRef.current.getBoundingClientRect();

      const absoluteTop = rect.top + window.scrollY;
      const absoluteLeft = rect.left + window.scrollX;

      Object.assign(activeContainer.style, {
        top: `${absoluteTop}px`,
        left: `${absoluteLeft}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        visibility: rect.height > 0 ? 'visible' : 'hidden',
        pointerEvents: rect.height > 0 ? 'auto' : 'none',
      });
    };

    const setup = async () => {
      const { container } = await getThemedChat({
        theme,
        user,
        instanceID,
      });

      activeContainer = container;
      alignGhost();
      setIsLoaded(true);
    };

    setup();

    const resizer = new ResizeObserver(alignGhost);
    if (shellRef.current) resizer.observe(shellRef.current);

    window.addEventListener('scroll', alignGhost, { passive: true });
    window.addEventListener('resize', alignGhost);

    const bodyResizer = new ResizeObserver(alignGhost);
    bodyResizer.observe(document.body);

    return () => {
      resizer.disconnect();
      bodyResizer.disconnect();
      window.removeEventListener('scroll', alignGhost);
      window.removeEventListener('resize', alignGhost);
      if (activeContainer) {
        activeContainer.style.visibility = 'hidden';
        activeContainer.style.pointerEvents = 'none';
        activeContainer.style.top = '-10000px';
      }
      setIsLoaded(false);
    };
  }, [theme, user, instanceID]);

  return { shellRef, isLoaded };
};

export default useTalkJS;
